import axios from 'axios';
import authService from './authService';

const DIRECTUS_URL = 'http://localhost/cms';

class CommentService {
  // Create a new comment
  async createComment(postId, userId, content) {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Ensure postId is an integer
      const postIdInt = parseInt(postId, 10);
      if (isNaN(postIdInt)) {
        throw new Error('Invalid post ID');
      }

      // Validate content
      const trimmedContent = content.trim();
      if (!trimmedContent) {
        throw new Error('Comment cannot be empty');
      }
      if (trimmedContent.length > 2000) {
        throw new Error('Comment cannot exceed 2000 characters');
      }

      const response = await axios.post(
        `${DIRECTUS_URL}/items/post_comments`,
        {
          post: postIdInt,
          user: userId,
          content: trimmedContent,
          status: 'published',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return {
        success: true,
        comment: response.data.data,
      };
    } catch (error) {
      console.error('Create comment error:', error);
      throw new Error(error.response?.data?.errors?.[0]?.message || 'Failed to post comment');
    }
  }

  // Get all comments for a post with user details
  async getCommentsByPost(postId) {
    try {
      const postIdInt = parseInt(postId, 10);
      if (isNaN(postIdInt)) {
        throw new Error('Invalid post ID');
      }

      const response = await axios.get(
        `${DIRECTUS_URL}/items/post_comments?filter[post][_eq]=${postIdInt}&filter[status][_eq]=published&fields=*,user.id,user.first_name,user.last_name,user.avatar&sort=date_created`
      );

      return response.data.data || [];
    } catch (error) {
      console.error('Get comments error:', error);
      return [];
    }
  }

  // Update a comment (edit content)
  async updateComment(commentId, content) {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Validate content
      const trimmedContent = content.trim();
      if (!trimmedContent) {
        throw new Error('Comment cannot be empty');
      }
      if (trimmedContent.length > 2000) {
        throw new Error('Comment cannot exceed 2000 characters');
      }

      const response = await axios.patch(
        `${DIRECTUS_URL}/items/post_comments/${commentId}`,
        {
          content: trimmedContent,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return {
        success: true,
        comment: response.data.data,
      };
    } catch (error) {
      console.error('Update comment error:', error);
      throw new Error(error.response?.data?.errors?.[0]?.message || 'Failed to update comment');
    }
  }

  // Delete a comment
  async deleteComment(commentId) {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      await axios.delete(`${DIRECTUS_URL}/items/post_comments/${commentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
      };
    } catch (error) {
      console.error('Delete comment error:', error);
      throw new Error(error.response?.data?.errors?.[0]?.message || 'Failed to delete comment');
    }
  }

  // Get comment count for a post
  async getCommentCount(postId) {
    try {
      const postIdInt = parseInt(postId, 10);
      if (isNaN(postIdInt)) {
        throw new Error('Invalid post ID');
      }

      const response = await axios.get(
        `${DIRECTUS_URL}/items/post_comments?filter[post][_eq]=${postIdInt}&filter[status][_eq]=published&aggregate[count]=id`
      );

      return response.data.data?.[0]?.count?.id || 0;
    } catch (error) {
      console.error('Get comment count error:', error);
      return 0;
    }
  }

  // Format relative time (e.g., "2 hours ago", "3 days ago")
  formatRelativeTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
  }
}

export default new CommentService();
